import { useEffect, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Image, Row, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { HiArrowLeft } from 'react-icons/hi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';
import Header from "../../Layout/Header";
import { useAppConfig } from '../../Utils/AppConfigContext';
import { getEndpointUrl } from "../../Utils/Common";

const Options = () => {
    const navigate = useNavigate();
    const { config, refreshConfig } = useAppConfig();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState({
        logo: false,
        signature: false,
        signature_stamp: false
    });
    const [alert, setAlert] = useState({ show: false, variant: '', message: '' });
    const [formData, setFormData] = useState({
        app_name: '',
        signature_name: '',
        signature_role: '',
        bank_name: '',
        bank_account_number: '',
        bank_holder_name: '',
        offer_tnc: ''
    });

    // Load initial data
    useEffect(() => {
        if (config) {
            setFormData({
                app_name: config.app_name || '',
                signature_name: config.signature_name || '',
                signature_role: config.signature_role || '',
                bank_name: config.bank_name || '',
                bank_account_number: config.bank_account_number || '',
                bank_holder_name: config.bank_holder_name || '',
                offer_tnc: config.offer_tnc || ''
            });
        }
    }, [config]);

    // React Quill configuration
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['link']
        ],
    };

    const quillFormats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image', 'color', 'background',
        'align', 'script'
    ];

    const showAlert = (variant, message) => {
        setAlert({ show: true, variant, message });
        setTimeout(() => setAlert({ show: false, variant: '', message: '' }), 5000);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handler khusus untuk React Quill
    const handleQuillChange = (content) => {
        setFormData(prev => ({
            ...prev,
            offer_tnc: content
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const options = Object.entries(formData).map(([key, value]) => ({
                option_name: key,
                option_value: value
            }));

            const response = await fetch(getEndpointUrl() + '/v2/options/batch', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ options })
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                showAlert('success', 'Pengaturan berhasil disimpan!');
                refreshConfig(); // Refresh app config
            } else {
                showAlert('danger', result.message || 'Gagal menyimpan pengaturan');
            }
        } catch (error) {
            showAlert('danger', 'Terjadi kesalahan: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (file, endpoint, fileField, uploadType) => {
        if (!file) return;

        setUploading(prev => ({ ...prev, [uploadType]: true }));

        try {
            const formData = new FormData();
            formData.append(fileField, file);

            const response = await fetch(getEndpointUrl() + '/v2' + endpoint, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                showAlert('success', `${uploadType === 'logo' ? 'Logo' : uploadType === 'signature' ? 'Tanda tangan' : 'Stempel'} berhasil diupload!`);
                refreshConfig(); // Refresh app config to get new URLs
            } else {
                showAlert('danger', result.message || 'Gagal mengupload file');
            }
        } catch (error) {
            showAlert('danger', 'Terjadi kesalahan: ' + error.message);
        } finally {
            setUploading(prev => ({ ...prev, [uploadType]: false }));
        }
    };

    return (
        <>
            <Helmet>
                <title>Pengaturan Umum - Car Inspector</title>
            </Helmet>
            <style>
                {`
                    .ql-editor {
                        min-height: 200px;
                        font-size: 14px;
                        line-height: 1.5;
                    }
                    .ql-toolbar {
                        border-top: 1px solid #ccc;
                        border-left: 1px solid #ccc;
                        border-right: 1px solid #ccc;
                    }
                    .ql-container {
                        border-bottom: 1px solid #ccc;
                        border-left: 1px solid #ccc;
                        border-right: 1px solid #ccc;
                    }
                    .quill {
                        background: white;
                    }
                `}
            </style>
            <Header />
            <Container>
                <Row>
                    <Col md={12} className="content mt-4">
                        <div className="content-header d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => navigate(-1)}
                                    className="me-3"
                                >
                                    <HiArrowLeft /> Kembali
                                </Button>
                                <strong className="me-auto navTitle">Pengaturan Umum</strong>
                            </div>
                        </div>

                        {alert.show && (
                            <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false })}>
                                {alert.message}
                            </Alert>
                        )}

                        <Row>
                            <Col lg={8}>
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Informasi Aplikasi & Perusahaan</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Form onSubmit={handleSubmit}>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nama Aplikasi</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="app_name"
                                                            value={formData.app_name}
                                                            onChange={handleInputChange}
                                                            placeholder="Masukkan nama aplikasi"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nama Penandatangan</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="signature_name"
                                                            value={formData.signature_name}
                                                            onChange={handleInputChange}
                                                            placeholder="Masukkan nama penandatangan"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Jabatan Penandatangan</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="signature_role"
                                                            value={formData.signature_role}
                                                            onChange={handleInputChange}
                                                            placeholder="Masukkan jabatan"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nama Bank</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="bank_name"
                                                            value={formData.bank_name}
                                                            onChange={handleInputChange}
                                                            placeholder="Masukkan nama bank"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nomor Rekening</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="bank_account_number"
                                                            value={formData.bank_account_number}
                                                            onChange={handleInputChange}
                                                            placeholder="Masukkan nomor rekening"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Nama Pemegang Rekening</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            name="bank_holder_name"
                                                            value={formData.bank_holder_name}
                                                            onChange={handleInputChange}
                                                            placeholder="Masukkan nama pemegang rekening"
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>

                                            <Form.Group className="mb-4">
                                                <Form.Label>Syarat dan Ketentuan</Form.Label>
                                                <ReactQuill
                                                    theme="snow"
                                                    value={formData.offer_tnc}
                                                    onChange={handleQuillChange}
                                                    modules={quillModules}
                                                    formats={quillFormats}
                                                    placeholder="Masukkan syarat dan ketentuan..."
                                                    style={{ 
                                                        height: '200px', 
                                                        marginBottom: '50px' 
                                                    }}
                                                />
                                                <Form.Text className="text-muted">
                                                    Gunakan toolbar di atas untuk memformat teks dengan mudah
                                                </Form.Text>
                                            </Form.Group>

                                            <div className="d-grid">
                                                <Button 
                                                    variant="primary" 
                                                    type="submit" 
                                                    disabled={loading}
                                                    size="lg"
                                                >
                                                    {loading ? (
                                                        <>
                                                            <Spinner size="sm" className="me-2" />
                                                            Menyimpan...
                                                        </>
                                                    ) : (
                                                        'Simpan Pengaturan'
                                                    )}
                                                </Button>
                                            </div>
                                        </Form>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col lg={4}>
                                {/* Logo Upload */}
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">Logo Perusahaan</h6>
                                    </Card.Header>
                                    <Card.Body className="text-center">
                                        {config?.logo_filename && (
                                            <Image 
                                                src={config.logo_filename} 
                                                alt="Logo" 
                                                fluid 
                                                className="mb-3"
                                                style={{ maxHeight: '100px' }}
                                            />
                                        )}
                                        <Form.Group>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(
                                                    e.target.files[0], 
                                                    '/options/upload_logo', 
                                                    'logo', 
                                                    'logo'
                                                )}
                                                disabled={uploading.logo}
                                            />
                                            {uploading.logo && (
                                                <div className="mt-2">
                                                    <Spinner size="sm" /> Mengupload...
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Card.Body>
                                </Card>

                                {/* Signature Upload */}
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">Tanda Tangan</h6>
                                    </Card.Header>
                                    <Card.Body className="text-center">
                                        {config?.signature_filename && (
                                            <Image 
                                                src={config.signature_filename} 
                                                alt="Signature" 
                                                fluid 
                                                className="mb-3"
                                                style={{ maxHeight: '80px' }}
                                            />
                                        )}
                                        <Form.Group>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(
                                                    e.target.files[0], 
                                                    '/options/upload_signature', 
                                                    'signature', 
                                                    'signature'
                                                )}
                                                disabled={uploading.signature}
                                            />
                                            {uploading.signature && (
                                                <div className="mt-2">
                                                    <Spinner size="sm" /> Mengupload...
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Card.Body>
                                </Card>

                                {/* Signature Stamp Upload */}
                                <Card>
                                    <Card.Header>
                                        <h6 className="mb-0">Stempel Tanda Tangan</h6>
                                    </Card.Header>
                                    <Card.Body className="text-center">
                                        {config?.signature_stamp_filename && (
                                            <Image 
                                                src={config.signature_stamp_filename} 
                                                alt="Signature Stamp" 
                                                fluid 
                                                className="mb-3"
                                                style={{ maxHeight: '100px' }}
                                            />
                                        )}
                                        <Form.Group>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => handleFileUpload(
                                                    e.target.files[0], 
                                                    '/options/upload_signature_stamp', 
                                                    'signature_stamp', 
                                                    'signature_stamp'
                                                )}
                                                disabled={uploading.signature_stamp}
                                            />
                                            {uploading.signature_stamp && (
                                                <div className="mt-2">
                                                    <Spinner size="sm" /> Mengupload...
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Options;